import { Add, CardGiftcard, Inventory2, Receipt } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  ColorPaletteProp,
  Link,
  ListItemDecorator,
  Sheet,
  Stack,
  Table,
  TabList,
  Tabs,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/joy";
import { Outlet, useNavigate, useParams } from "@tanstack/react-router";
import { HTTPError } from "griptonite-api-client/client";
import {
  ApiCreditProductAdminListItem,
  ApiCreditSystemAdmin,
  CreditFulfilmentType,
  CreditRewardCategory,
} from "griptonite-api-client/types";
import { useMemo, useState } from "react";

import { useCreditSystemProductsQuery, useVenueCreditSystemQuery } from "@/hooks/credits";
import { useCurrentVenueQuery } from "@/hooks/venues";
import getThumbnailUrl from "@/utils/getThumbnailUrl";
import pluralize from "@/utils/pluralize";

import { PillsSelect } from "../forms/PillsSelect";
import { ButtonLink, TabLink } from "../Links";
import { SUPPORT_EMAIL } from "../ManageCreditSystem/ManageCreditSystem";
import LoyaltyNavToggle from "./LoyaltyNavToggle";
import RedemptionsLog from "./RedemptionsLog";
import { REWARD_CATEGORIES, REWARD_CATEGORY_LABELS } from "./rewardCategories";

// One cutoff for every reward until the API lets admins set their own.
const LOW_STOCK_THRESHOLD = 5;

const ALL_CATEGORIES = "ALL";

type Scope = "venue" | "all";

const RewardImage = ({ product }: { product: ApiCreditProductAdminListItem }) => (
  <Box
    sx={{
      width: 48,
      height: 48,
      flexShrink: 0,
      borderRadius: "md",
      overflow: "hidden",
      backgroundColor: "neutral.100",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "neutral.400",
    }}
  >
    {product.image ? (
      <img
        alt=""
        src={getThumbnailUrl(product.image.url)}
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    ) : (
      <CardGiftcard />
    )}
  </Box>
);

const StockChip = ({
  stock,
  fulfilmentType,
}: {
  stock: number | null;
  fulfilmentType: CreditFulfilmentType;
}) => {
  // Only a `STICKER` reward can be unlimited; a `CODE` reward's stock is always a count.
  if (stock === null) {
    return (
      <Chip variant="soft" color="neutral">
        Unlimited
      </Chip>
    );
  }
  let color: ColorPaletteProp = "success";
  let label = fulfilmentType === "CODE" ? pluralize(stock, "code") : `${stock} left`;
  if (stock === 0) {
    color = "danger";
    label = "Sold out";
  } else if (stock <= LOW_STOCK_THRESHOLD) {
    color = "warning";
    label += " · low";
  }
  return (
    <Chip variant="soft" color={color} sx={{ fontWeight: "bold" }}>
      {label}
    </Chip>
  );
};

export const OfferedAt = ({
  venueUuids,
  system,
}: {
  venueUuids: string[];
  system: ApiCreditSystemAdmin;
}) => {
  const names = system.venues
    .filter((venue) => venueUuids.includes(venue.uuid))
    .map((venue) => venue.name);
  if (names.length === 0) {
    return <Typography textColor="neutral.500">No gyms</Typography>;
  }
  if (names.length === 1) {
    return <Typography>{names[0]}</Typography>;
  }
  const label =
    names.length === system.venues.length
      ? `All ${names.length} gyms`
      : pluralize(names.length, "gym");
  return (
    <Tooltip title={names.join(", ")} variant="outlined">
      <Typography sx={{ textDecoration: "underline dotted", width: "fit-content" }}>
        {label}
      </Typography>
    </Tooltip>
  );
};

// Admins and staff open a reward in the side panel by clicking its row: admins to edit and stock
// it, staff to stock it.
const RewardsTable = ({
  products,
  system,
  venueUuid,
  canOpen,
}: {
  products: ApiCreditProductAdminListItem[];
  system: ApiCreditSystemAdmin;
  venueUuid: string;
  canOpen: boolean;
}) => {
  const navigate = useNavigate();
  const openRewardUuid = useParams({ strict: false }).rewardUuid;

  return (
    <Sheet variant="outlined" sx={{ borderRadius: "md", overflow: "hidden" }}>
      <Table
        hoverRow={canOpen}
        sx={{
          "--TableCell-paddingX": "16px",
          "--TableCell-paddingY": "12px",
          "& thead th": { verticalAlign: "middle" },
          "& tbody td": { verticalAlign: "middle" },
          ...(canOpen ? { "& tbody tr": { cursor: "pointer" } } : {}),
          "& tbody tr.open": { backgroundColor: "primary.50" },
          "--TableRow-hoverBackground": (theme) => theme.vars.palette.neutral["100"],
        }}
      >
        <thead>
          <tr>
            <th style={{ width: "36%" }}>Reward</th>
            <th>Category</th>
            <th>Cost</th>
            <th>Stock here</th>
            {system.venues.length > 1 ? <th>Offered at</th> : null}
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product) => (
            <tr
              key={product.uuid}
              className={product.uuid === openRewardUuid ? "open" : undefined}
              style={{ opacity: product.isActive ? 1 : 0.6 }}
              onClick={
                canOpen
                  ? () =>
                      navigate({
                        to: "/gym/$venueUuid/loyalty/rewards/$rewardUuid",
                        params: { venueUuid, rewardUuid: product.uuid },
                      })
                  : undefined
              }
            >
              <td>
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <RewardImage product={product} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="title-md">{product.name}</Typography>
                    {product.description ? (
                      <Typography level="body-sm" textColor="neutral.500" noWrap>
                        {product.description}
                      </Typography>
                    ) : null}
                  </Box>
                </Stack>
              </td>
              <td>{REWARD_CATEGORY_LABELS[product.category] ?? product.category}</td>
              <td>
                <Typography fontWeight="bold">{pluralize(product.cost, "credit")}</Typography>
              </td>
              <td>
                {product.venues.includes(venueUuid) ? (
                  <StockChip stock={product.stock} fulfilmentType={system.fulfilmentType} />
                ) : (
                  <Typography textColor="neutral.500">Not sold here</Typography>
                )}
              </td>
              {system.venues.length > 1 ? (
                <td>
                  <OfferedAt venueUuids={product.venues} system={system} />
                </td>
              ) : null}
              <td>
                <Chip
                  variant="soft"
                  color={product.isActive ? "success" : "neutral"}
                  sx={{ fontWeight: "bold" }}
                >
                  {product.isActive ? "Enabled" : "Disabled"}
                </Chip>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Sheet>
  );
};

// The Log tab lists what members have redeemed, in place of the rewards themselves.
export default function RewardsOverview({ tab }: { tab: "rewards" | "log" }) {
  const { data: venue } = useCurrentVenueQuery();
  const isAdmin = venue?.userRole === "ADMIN";
  const systemQuery = useVenueCreditSystemQuery(venue?.uuid);
  const system = systemQuery.data;
  const productsQuery = useCreditSystemProductsQuery(system?.slug, venue?.uuid);

  // Staff only get this gym's rewards, so the toggle is just for admins, and only when the system
  // has other gyms whose rewards could show.
  const hasOtherGyms = (system?.venues.length ?? 0) > 1;
  const [scope, setScope] = useState<Scope>("venue");
  const [showDisabled, setShowDisabled] = useState(true);
  const [category, setCategory] = useState<CreditRewardCategory | null>(null);

  // Only categories that some reward uses, so every chip finds something.
  const categories = useMemo(
    () =>
      REWARD_CATEGORIES.filter((key) =>
        productsQuery.data?.some((product) => product.category === key),
      ),
    [productsQuery.data],
  );

  const products = useMemo(
    () =>
      productsQuery.data?.filter(
        (product) =>
          (showDisabled || product.isActive) &&
          (scope === "all" || product.venues.includes(venue!.uuid)) &&
          (!category || product.category === category),
      ),
    [productsQuery.data, showDisabled, scope, venue, category],
  );

  let content;
  if (systemQuery.error instanceof HTTPError && systemQuery.error.response.status === 404) {
    content = (
      <Alert color="neutral">
        <Typography>
          Credits aren’t set up for this gym yet. To get started, contact{" "}
          <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>.
        </Typography>
      </Alert>
    );
  } else if (systemQuery.error || productsQuery.error) {
    content = <Alert color="danger">Couldn’t load the rewards.</Alert>;
  } else if (system && venue && products) {
    if (!productsQuery.data?.length) {
      content = (
        <Typography textColor="neutral.500">
          No rewards yet.{" "}
          {isAdmin ? "Add one to give climbers something to spend their credits on." : null}
        </Typography>
      );
    } else if (!products.length) {
      content = <Typography textColor="neutral.500">No rewards match these filters.</Typography>;
    } else {
      content = (
        <RewardsTable
          products={products}
          system={system}
          venueUuid={venue.uuid}
          canOpen={isAdmin || venue.userRole === "STAFF"}
        />
      );
    }
  }

  // The panel for a new or chosen reward renders in the outlet, beside the table.
  return (
    <Stack direction="row" sx={{ height: "100%" }}>
      <Stack sx={{ flex: 1, minWidth: 0, height: "100%" }}>
        <Tabs value={tab}>
          <TabList>
            <TabLink
              value="rewards"
              sx={{ flex: 1, height: 40 }}
              from="/gym/$venueUuid/loyalty/rewards"
              to="."
              search={{}}
              replace
            >
              <ListItemDecorator>
                <Inventory2 />
              </ListItemDecorator>
              Rewards
            </TabLink>
            <TabLink
              value="log"
              sx={{ flex: 1, height: 40 }}
              from="/gym/$venueUuid/loyalty/rewards"
              to="."
              search={{ tab: "log" }}
              replace
            >
              <ListItemDecorator>
                <Receipt />
              </ListItemDecorator>
              Log
            </TabLink>
          </TabList>
        </Tabs>
        <Stack gap={3} sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Stack direction="row" alignItems="center" gap={1}>
              <LoyaltyNavToggle />
              <Typography level="h3">{tab === "log" ? "Redemptions log" : "Rewards"}</Typography>
            </Stack>
            {isAdmin ? (
              <Stack direction="row" alignItems="center" gap={2}>
                {hasOtherGyms ? (
                  <ToggleButtonGroup
                    value={scope}
                    onChange={(_, value) => value && setScope(value)}
                  >
                    <Button value="venue">This gym</Button>
                    <Button value="all">All gyms</Button>
                  </ToggleButtonGroup>
                ) : null}
                {tab === "rewards" ? (
                  <ButtonLink
                    from="/gym/$venueUuid/loyalty/rewards"
                    to="./create"
                    startDecorator={<Add />}
                  >
                    New reward
                  </ButtonLink>
                ) : null}
              </Stack>
            ) : null}
          </Stack>
          {tab === "log" ? (
            <RedemptionsLog allGyms={scope === "all"} />
          ) : (
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                <Box>
                  {categories.length > 1 ? (
                    <PillsSelect
                      options={[
                        { name: "All", value: ALL_CATEGORIES },
                        ...categories.map((key) => ({
                          name: REWARD_CATEGORY_LABELS[key] ?? key,
                          value: key,
                        })),
                      ]}
                      value={category ?? ALL_CATEGORIES}
                      // Clicking the selected pill clears it, which also means "All".
                      onChange={(value) =>
                        setCategory(
                          value && value !== ALL_CATEGORIES
                            ? (value as CreditRewardCategory)
                            : null,
                        )
                      }
                    />
                  ) : null}
                </Box>
                <Checkbox
                  label="Show disabled"
                  checked={showDisabled}
                  onChange={(event) => setShowDisabled(event.target.checked)}
                />
              </Stack>
              {content}
            </>
          )}
        </Stack>
      </Stack>
      {tab === "rewards" ? <Outlet /> : null}
    </Stack>
  );
}
